/**
 * Organisation Management service
 * Handles user management, organisations, assignments, and profiles
 * Combines JLoo authentication and Studio token authorization
 */

import axios, { type AxiosResponse } from 'axios'
import BaseService from './BaseService'
import type { Organisation, OrganisationRole, User, UserInvitationStatus, UserPromotion } from '@/types/models'
import { require } from '@/utils/guards'

// ============================================================================
// Request/Response Types
// ============================================================================

/** Organisations list response */
export interface OrganisationsListResponse {
  organisations: Organisation[]
}

/** Generate token request */
export interface GenerateTokenRequest {
  organisationId: string
}

/** Generate token response */
export interface GenerateTokenResponse {
  access_token: string
  expires_in: number
  token_type: string
}

/** User details response (from getUserDetails) */
export interface UserDetailsResponse extends User {}

/** Organisation assignments response */
export interface OrganisationAssignmentsResponse {
  ids: number[]  // Array of accommodation IDs
}

/** Post assignment request */
export interface PostAssignmentRequest {
  accommodationId: number
  isOnboarding?: boolean
}

/** Organisation users response */
export interface OrganisationUsersResponse {
  users: Array<{
    id: string
    email: string
    role: OrganisationRole
    firstName?: string
    lastName?: string
  }>
}

/** Invite user request */
export interface InviteUserOrgRequest {
  data: {
    invitedEmail: string
    role?: Exclude<OrganisationRole, 'support'>
    language: string
  }
}

/** User invitations response */
export interface UserInvitationsResponse {
  invitations: Array<{
    organisationId: string
    invitedEmail: string
    userInvitationStatus: UserInvitationStatus
    role: OrganisationRole
  }>
}

/** Remove user from organisation request */
export interface RemoveUserRequest {
  organisationId: string
}

/** Remove user invitation request (email object) */
export interface RemoveInvitationRequest {
  invitedEmail: string
}

/** Remove existing user request */
export interface RemoveExistingUserRequest {
  userId: string
}

/** Update user role request */
export interface UpdateUserRoleRequest {
  userId: string
  role: OrganisationRole
}

/** Phone validation request */
export interface ValidatePhoneRequest {
  phoneRegionCode: string
  phoneNationalNumber: string
}

/** Phone validation response */
export interface ValidatePhoneResponse {
  valid: boolean
  message?: string
}

/** Update user profile request */
export interface UpdateUserProfileRequest {
  userId: string
  body: Partial<User>
}

/** Create user profile request */
export interface CreateUserProfileRequest {
  body: Omit<User, 'id' | 'userId'>
}

/** Update organisation name request */
export interface UpdateOrganisationNameRequest {
  name: string
  organisationId?: string
}

/** Update promotion status request */
export interface UpdatePromotionStatusRequest {
  promotionType: string
  status: string
}

/** Invite user response */
export interface InviteUserResponse {
  userAddedToOrganisation: boolean
  userInvited: boolean
  invitationId: string | null
}

// ============================================================================
// OrganisationManagementService Class
// ============================================================================

/**
 * Organisation and user management service
 * Provides comprehensive org/user CRUD operations
 */
class OrganisationManagementService extends BaseService {
  constructor() {
    super({ baseUrl: import.meta.env.VITE_ORGANISATION_MANAGEMENT_API_URL })
  }

  /**
   * Get list of organisations for current user
   * @returns Promise with organisations list
   */
  getOrganisationsList(): Promise<AxiosResponse<OrganisationsListResponse>> {
    return axios({
      url: `${this.baseUrl}/organisations`,
      method: 'GET',
      headers: this.jloHeaders(),
    })
  }

  /**
   * Generate Studio token for organisation
   * @param request - Organisation ID
   * @returns Promise with Studio token
   * @throws Error if organisationId missing
   */
  postGenerateToken({ organisationId }: GenerateTokenRequest): Promise<AxiosResponse<GenerateTokenResponse>> {
    require(organisationId)

    return axios({
      url: `${this.baseUrl}/auth/generate_token`,
      method: 'POST',
      headers: this.jloHeaders(),
      data: {
        organisationId,
      },
    })
  }

  /**
   * Get current user details
   * @returns Promise with user details
   */
  getUserDetails(): Promise<AxiosResponse<UserDetailsResponse>> {
    return axios({
      url: `${this.baseUrl}/users`,
      method: 'GET',
      headers: this.jloHeaders(),
    })
  }

  /**
   * Get accommodation assignments for organisation
   * @param request - Organisation ID
   * @returns Promise with assignment IDs
   */
  getOrganisationAssignments({ organisationId }: { organisationId: string }): Promise<AxiosResponse<OrganisationAssignmentsResponse>> {
    return axios({
      url: `${this.baseUrl}/organisations/${organisationId}/assignments?pageSize=${10000}`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Create accommodation assignment
   * @param request - Assignment data
   * @returns Promise with assignment result
   */
  postOrganisationAssignments({ accommodationId, isOnboarding }: PostAssignmentRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: isOnboarding
        ? `${this.baseUrl}/organisations/assignments?isOnboarding=true`
        : `${this.baseUrl}/organisations/assignments`,
      method: 'POST',
      data: { accommodationId },
      headers: this.studioHeaders(),
    })
  }

  /**
   * Get users in organisation
   * @param request - Organisation ID
   * @returns Promise with organisation users
   */
  getOrganisationUsers({ organisationId }: { organisationId: string }): Promise<AxiosResponse<OrganisationUsersResponse>> {
    require(organisationId)

    return axios({
      url: `${this.baseUrl}/organisations/${organisationId}/users`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Invite user to organisation
   * @param request - Invitation data
   * @returns Promise with invitation result
   */
  inviteUser({ data }: InviteUserOrgRequest): Promise<AxiosResponse<InviteUserResponse>> {
    return axios({
      url: `${this.baseUrl}/organisations/users`,
      method: 'POST',
      data: data,
      headers: this.studioHeaders(),
    })
  }

  /**
   * Get user invitations
   * @returns Promise with user's pending invitations
   */
  getUserInvitations(): Promise<AxiosResponse<UserInvitationsResponse>> {
    return axios({
      url: `${this.baseUrl}/organisations/invitations`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Remove user from organisation
   * @param request - Organisation ID
   * @returns Promise with removal result
   * @throws Error if organisationId missing
   */
  removeUserFromOrganisation({ organisationId }: RemoveUserRequest): Promise<AxiosResponse<void>> {
    require(organisationId)

    return axios({
      url: `${this.baseUrl}/organisations/${organisationId}/users`,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Remove user invitation from organisation
   * @param email - Invitation email to remove
   * @returns Promise with removal result
   */
  removeUserInvitationFromOrganisation(email: RemoveInvitationRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${this.baseUrl}/organisations/users`,
      data: email,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Remove existing user from organisation
   * @param request - User ID to remove
   * @returns Promise with removal result
   * @throws Error if userId missing
   */
  removeExistingUserFromOrganisation({ userId }: RemoveExistingUserRequest): Promise<AxiosResponse<void>> {
    require(userId)

    return axios({
      url: `${this.baseUrl}/organisations/users/${userId}`,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }

  /**
   * Update user role in organisation
   * @param request - User ID and new role
   * @returns Promise with update result
   */
  updateUserRoleInOrganisation({ userId, role }: UpdateUserRoleRequest): Promise<AxiosResponse<void>> {
    require(userId)
    require(role)

    return axios({
      url: `${this.baseUrl}/organisations/users/${userId}/roles`,
      method: 'PUT',
      data: { role },
      headers: this.studioHeaders(),
    })
  }

  /**
   * Validate phone number format
   * @param body - Phone validation request
   * @returns Promise with validation result
   */
  validatePhoneNumber(body: ValidatePhoneRequest): Promise<AxiosResponse<ValidatePhoneResponse>> {
    return axios({
      url: `${this.baseUrl}/validation/phone_number`,
      method: 'POST',
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Update user profile
   * @param request - User ID and profile data
   * @returns Promise with update result
   */
  updateUserProfile({ userId, body }: UpdateUserProfileRequest): Promise<AxiosResponse<User>> {
    return axios({
      url: `${this.baseUrl}/users/${userId}`,
      method: 'PUT',
      data: body,
      headers: this.studioHeaders(),
    })
  }

  /**
   * Create new user profile
   * @param request - User profile data
   * @returns Promise with created user
   */
  createUserProfile({ body }: CreateUserProfileRequest): Promise<AxiosResponse<User>> {
    return axios({
      url: `${this.baseUrl}/users`,
      method: 'POST',
      data: body,
      headers: this.jloHeaders(),
    })
  }

  /**
   * Update organisation name
   * @param body - Organisation update data
   * @returns Promise with update result
   */
  updateOrganisationName(body: UpdateOrganisationNameRequest): Promise<AxiosResponse<Organisation>> {
    return axios({
      url: `${this.baseUrl}/organisations`,
      method: 'PUT',
      data: body,
      headers: this.studioHeaders(),
    })
  }

  /**
   * Update promotion/onboarding status
   * @param request - Promotion type and status
   * @returns Promise with update result
   * @throws Error if promotionType or status missing
   */
  updatePromotionStatus({ promotionType, status }: UpdatePromotionStatusRequest): Promise<AxiosResponse<UserPromotion>> {
    require(promotionType)
    require(status)

    return axios({
      url: `${this.baseUrl}/users/promotions/${promotionType}`,
      method: 'POST',
      data: {
        interaction: status,
      },
      headers: this.studioHeaders(),
    })
  }
}

/** Singleton organisation management service instance */
export default new OrganisationManagementService()
